import React from 'react';
import {useTranslation} from 'react-i18next';

import {addMonths} from 'date-fns/addMonths';
import {addYears} from 'date-fns/addYears';
import {isSameMonth} from 'date-fns/isSameMonth';
import {subYears} from 'date-fns/subYears';

import ArrowForwardIcon from '@edna/icons/arrowDoubleForward.svg';
import ArrowRewindIcon from '@edna/icons/arrowDoubleRewind.svg';
import ArrowLeftIcon from '@edna/icons/keyboardArrowLeft.svg';
import ArrowRightIcon from '@edna/icons/keyboardArrowRight.svg';

import Button from '../Button';
import {CalendarContext} from './CalendarContext';
import {Decade, Month, Year} from './Periods';
import {TCalendarRangeValue} from './index';
import * as S from './style';
import {
  ECalendarLeadDate,
  ECalendarPeriod,
  ECalendarType,
  TCalendarDate,
  TCalendarType,
  TWeekStart,
  YEARS_IN_DECADE,
} from './types';
import {getAvailableDate, isRangeValue} from './utils';

const getViewDate = (
  startDate: Date | null,
  endDate: Date | null,
  leadDate: ECalendarLeadDate,
): Date | null => {
  if (leadDate === ECalendarLeadDate.END) {
    return endDate || startDate;
  }

  return startDate;
};

const getPropValueCalendarDate = (value?: TCalendarDate | null): TCalendarRangeValue => {
  if (!isRangeValue(value)) {
    return {startDate: value ?? null, endDate: null};
  }

  if (value.startDate && value.endDate) {
    return {
      startDate: value.startDate > value.endDate ? value.endDate : value.startDate,
      endDate: value.endDate < value.startDate ? value.startDate : value.endDate,
    };
  }

  return {
    startDate: value.startDate,
    endDate: value.endDate,
  };
};

export type TProps = {
  className?: string;
  value?: TCalendarDate | null;
  startDate?: Date | null;
  endDate?: Date | null;
  calendarType: TCalendarType;
  now?: Date;
  minDate?: Date | null;
  maxDate?: Date | null;
  weekStart?: TWeekStart;
  isTwoMonthsView?: boolean;
  hasAcceptButtons?: boolean;
  hasYearSwitchButton?: boolean;
  leadDate?: ECalendarLeadDate;
  onHoverChange?: (value: Date | null) => void;
  onChange?: (value: TCalendarDate) => void;
  onSubmit?: (value: TCalendarDate) => void;
  onCancel?: TEmptyFunction;
  dataSelector?: string;
};

export const Calendar = React.memo<TProps>(
  ({
    className,
    value: propValue = null,
    calendarType,
    now = new Date(),
    minDate = null,
    maxDate = null,
    weekStart = 1,
    leadDate: propLeadDate = ECalendarLeadDate.START,
    onHoverChange,
    onChange,
    onSubmit,
    onCancel,
    hasAcceptButtons,
    isTwoMonthsView = calendarType === ECalendarType.DATE_RANGE,
    hasYearSwitchButton = calendarType === ECalendarType.DATE_RANGE,
    dataSelector,
  }) => {
    const {t} = useTranslation();

    const [leadDate, setLeadDate] = React.useState<ECalendarLeadDate>(propLeadDate);
    const [startDate, setStartDate] = React.useState<Date | null>(
      getPropValueCalendarDate(propValue).startDate,
    );
    const [endDate, setEndDate] = React.useState<Date | null>(
      getPropValueCalendarDate(propValue).endDate,
    );
    const [viewDate, setViewDate] = React.useState<Date>(
      getViewDate(startDate, endDate, leadDate) || now,
    );
    const [viewPeriod, setViewPeriod] = React.useState<ECalendarPeriod>(ECalendarPeriod.MONTH);
    const [hoveredDate, setHoveredDate] = React.useState<Date | null>(null);

    React.useEffect(() => onHoverChange?.(hoveredDate), [hoveredDate]);

    React.useEffect(() => {
      const newDate = getPropValueCalendarDate(propValue);

      setStartDate(newDate.startDate);
      setEndDate(newDate.endDate);
    }, [propValue]);

    const incrementPeriod = React.useCallback(
      (period: ECalendarPeriod = viewPeriod) =>
        () =>
          setViewDate((date) => {
            if (period === ECalendarPeriod.DECADE) {
              return new Date(
                date.getFullYear() + YEARS_IN_DECADE,
                date.getMonth(),
                date.getDate(),
              );
            }

            if (period === ECalendarPeriod.YEAR) {
              return new Date(date.getFullYear() + 1, date.getMonth(), date.getDate());
            }

            if (period === ECalendarPeriod.MONTH) {
              return new Date(date.getFullYear(), date.getMonth() + 1, date.getDate());
            }

            return date;
          }),
      [viewPeriod],
    );

    const decrementPeriod = React.useCallback(
      (period: ECalendarPeriod = viewPeriod) =>
        () =>
          setViewDate((date) => {
            if (period === ECalendarPeriod.DECADE) {
              return new Date(
                date.getFullYear() - YEARS_IN_DECADE,
                date.getMonth(),
                date.getDate(),
              );
            }

            if (period === ECalendarPeriod.YEAR) {
              return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
            }

            if (period === ECalendarPeriod.MONTH) {
              return new Date(date.getFullYear(), date.getMonth() - 1, date.getDate());
            }

            return date;
          }),
      [viewPeriod],
    );

    const switchPeriod = React.useCallback(
      (period: ECalendarPeriod) => () => setViewPeriod(period),
      [],
    );

    const nextViewMonth = React.useMemo(
      () =>
        viewPeriod === ECalendarPeriod.MONTH && isTwoMonthsView ? addMonths(viewDate, 1) : null,
      [viewDate, isTwoMonthsView, viewPeriod],
    );

    const viewDecade = React.useMemo(() => {
      const startYear = Math.floor(viewDate.getFullYear() / 10) * 10;

      return {
        startYear,
        endYear: startYear + YEARS_IN_DECADE - 1,
      };
    }, [viewDate]);

    const isDecrementDisabled = React.useCallback(
      (period?: ECalendarPeriod) => {
        if (!minDate) {
          return false;
        }

        const targetPeriod = period ?? viewPeriod;

        if (targetPeriod === ECalendarPeriod.DECADE) {
          return viewDecade.startYear <= minDate.getFullYear();
        }

        if (targetPeriod === ECalendarPeriod.YEAR) {
          return viewPeriod === ECalendarPeriod.YEAR
            ? viewDate.getFullYear() <= minDate.getFullYear()
            : subYears(viewDate, 1) <= minDate;
        }

        return viewDate < minDate || isSameMonth(viewDate, minDate);
      },
      [viewDate, viewPeriod, viewDecade, minDate],
    );

    const isIncrementDisabled = React.useCallback(
      (period?: ECalendarPeriod) => {
        if (!maxDate) {
          return false;
        }

        const targetPeriod = period ?? viewPeriod;

        if (targetPeriod === ECalendarPeriod.DECADE) {
          return viewDecade.endYear >= maxDate.getFullYear();
        }

        const targetDate = nextViewMonth || viewDate;

        if (targetPeriod === ECalendarPeriod.YEAR) {
          return viewPeriod === ECalendarPeriod.YEAR
            ? viewDate.getFullYear() >= maxDate.getFullYear()
            : addYears(targetDate, 1) >= maxDate;
        }

        return targetDate > maxDate || isSameMonth(targetDate, maxDate);
      },
      [viewDate, viewPeriod, viewDecade, maxDate, nextViewMonth],
    );

    const handleSubmit = React.useCallback(() => {
      if (calendarType === ECalendarType.DATE_RANGE) {
        return onSubmit?.({startDate, endDate});
      }

      if (startDate) {
        onSubmit?.(startDate);
      }
    }, [onSubmit, startDate, endDate, calendarType]);

    const handleSelectMonth = React.useCallback((nextDate: Date) => {
      setViewDate(
        (prevViewDate) =>
          new Date(prevViewDate.getFullYear(), nextDate.getMonth(), prevViewDate.getDate()),
      );
      setViewPeriod(ECalendarPeriod.MONTH);
    }, []);

    const handleSelectYear = React.useCallback((nextDate: Date) => {
      setViewDate(
        (prevViewDate) =>
          new Date(nextDate.getFullYear(), prevViewDate.getMonth(), prevViewDate.getDate()),
      );
      setViewPeriod(ECalendarPeriod.YEAR);
    }, []);

    const handleSelect = React.useCallback(
      (nextDate: Date | null) => {
        if (!nextDate) {
          return;
        }

        const nextDates = getAvailableDate(nextDate, {
          startDate,
          endDate,
          minDate,
          maxDate,
          leadDate,
          calendarType,
        });

        setStartDate(nextDates.startDate);
        setEndDate(nextDates.endDate);
        setLeadDate(
          leadDate === ECalendarLeadDate.START ? ECalendarLeadDate.END : ECalendarLeadDate.START,
        );

        if (calendarType === ECalendarType.DATE_RANGE) {
          onChange?.(nextDates);

          if (!hasAcceptButtons && nextDates.startDate && nextDates.endDate) {
            onSubmit?.(nextDates);
          }

          return;
        }

        if (nextDates.startDate) {
          return hasAcceptButtons
            ? onChange?.(nextDates.startDate)
            : onSubmit?.(nextDates.startDate);
        }
      },
      [
        hasAcceptButtons,
        onSubmit,
        onChange,
        startDate,
        endDate,
        minDate,
        maxDate,
        leadDate,
        calendarType,
      ],
    );

    return (
      <S.Calendar className={className} data-selector={dataSelector}>
        <S.Head>
          <S.HeadSection
            hasYearSwitchButton={viewPeriod === ECalendarPeriod.MONTH && hasYearSwitchButton}
          >
            <S.HeadControls>
              {viewPeriod === ECalendarPeriod.MONTH && hasYearSwitchButton && (
                <S.Arrow
                  as={ArrowRewindIcon}
                  role="button"
                  aria-label="subYear"
                  disabled={isDecrementDisabled(ECalendarPeriod.YEAR)}
                  onClick={decrementPeriod(ECalendarPeriod.YEAR)}
                />
              )}
              <S.Arrow
                as={ArrowLeftIcon}
                role="button"
                aria-label="subMonth"
                disabled={isDecrementDisabled()}
                onClick={decrementPeriod()}
              />
            </S.HeadControls>
            <S.HeadDate data-selector="EdnaCalendar:head-date-current">
              {viewPeriod === ECalendarPeriod.DECADE &&
                `${viewDecade.startYear} - ${viewDecade.endYear}`}
              {viewPeriod === ECalendarPeriod.YEAR && (
                <S.DateLink onClick={switchPeriod(ECalendarPeriod.DECADE)}>
                  {viewDate.getFullYear()}
                </S.DateLink>
              )}
              {viewPeriod === ECalendarPeriod.MONTH && (
                <>
                  <S.DateLink onClick={switchPeriod(ECalendarPeriod.YEAR)}>
                    {t(`EdnaCalendar:monthsKeysByIndex.${viewDate.getMonth()}`)}
                  </S.DateLink>
                  <S.DateLink onClick={switchPeriod(ECalendarPeriod.DECADE)}>
                    {viewDate.getFullYear()}
                  </S.DateLink>
                </>
              )}
            </S.HeadDate>
            <S.HeadControls>
              {!nextViewMonth && (
                <>
                  <S.Arrow
                    as={ArrowRightIcon}
                    role="button"
                    aria-label="addMonth"
                    disabled={isIncrementDisabled()}
                    onClick={incrementPeriod()}
                  />
                  {viewPeriod === ECalendarPeriod.MONTH && hasYearSwitchButton && (
                    <S.Arrow
                      as={ArrowForwardIcon}
                      role="button"
                      aria-label="addYear"
                      disabled={isIncrementDisabled(ECalendarPeriod.YEAR)}
                      onClick={incrementPeriod(ECalendarPeriod.YEAR)}
                    />
                  )}
                </>
              )}
            </S.HeadControls>
          </S.HeadSection>
          {nextViewMonth && (
            <S.HeadSection hasYearSwitchButton={hasYearSwitchButton}>
              <S.HeadControls />
              <S.HeadDate data-selector="EdnaCalendar:head-date-next">
                <S.DateLink onClick={switchPeriod(ECalendarPeriod.YEAR)}>
                  {t(`EdnaCalendar:monthsKeysByIndex.${nextViewMonth.getMonth()}`)}
                </S.DateLink>
                <S.DateLink onClick={switchPeriod(ECalendarPeriod.DECADE)}>
                  {nextViewMonth.getFullYear()}
                </S.DateLink>
              </S.HeadDate>
              <S.HeadControls>
                <S.Arrow
                  as={ArrowRightIcon}
                  role="button"
                  aria-label="addMonth"
                  disabled={isIncrementDisabled()}
                  onClick={incrementPeriod()}
                />
                {hasYearSwitchButton && (
                  <S.Arrow
                    as={ArrowForwardIcon}
                    role="button"
                    aria-label="addYear"
                    disabled={isIncrementDisabled(ECalendarPeriod.YEAR)}
                    onClick={incrementPeriod(ECalendarPeriod.YEAR)}
                  />
                )}
              </S.HeadControls>
            </S.HeadSection>
          )}
        </S.Head>
        <S.Content>
          <CalendarContext.Provider
            value={{
              minDate,
              maxDate,
              startDate,
              endDate,
              leadDate,
              calendarType,
              now,
              hoveredDate,
              setHoveredDate,
            }}
          >
            {viewPeriod === ECalendarPeriod.DECADE && (
              <Decade onSelect={handleSelectYear} startYear={viewDecade.startYear} />
            )}
            {viewPeriod === ECalendarPeriod.YEAR && (
              <Year onSelect={handleSelectMonth} year={viewDate.getFullYear()} />
            )}
            {viewPeriod === ECalendarPeriod.MONTH && (
              <Month
                onSelect={handleSelect}
                month={viewDate.getMonth()}
                year={viewDate.getFullYear()}
                weekStart={weekStart}
                dataSelector="EdnaCalendar:month-current"
              />
            )}
            {nextViewMonth && (
              <Month
                onSelect={handleSelect}
                month={nextViewMonth.getMonth()}
                year={nextViewMonth.getFullYear()}
                weekStart={weekStart}
                dataSelector="EdnaCalendar:month-next"
              />
            )}
          </CalendarContext.Provider>
        </S.Content>
        {hasAcceptButtons && (
          <S.Actions>
            <Button onClick={onCancel} size="S">
              {t('EdnaCalendar:button.cancel')}
            </Button>
            <Button appearance="primary" onClick={handleSubmit} size="S">
              {t('EdnaCalendar:button.ok')}
            </Button>
          </S.Actions>
        )}
      </S.Calendar>
    );
  },
);
